/**
 * Catalog-style product add-on picker.
 *
 * Each grid card opens the theme's native modal-dialog/modal-opener
 * (assets/global.js) with a server-rendered variant picker — no AJAX
 * fetch, no extra slider library. Picking a variant tags the grid card
 * with `data-variant-id` and toggles `.selected` (for the active border),
 * which assets/add-on-product.js reads at cart-submit time to add it
 * alongside the main product.
 *
 * A product page can show more than one add-on group (multiple matching
 * `product_add_on_catalog` metaobject entries, each its own
 * `.addon-catalog-app`) — every lookup below is scoped to the clicked
 * card's own group so picking/declining in one group never touches another.
 */
(() => {
  const init = () => {
    const container = document.querySelector('.catalog-product-addon');
    if (!container) return;

    setupNoThanksButtons(container);
    setupVariantPickers();
  };

  const clearGroupSelection = (group) => {
    group?.querySelectorAll('.catalog-grid-card__item').forEach((card) => {
      card.classList.remove('selected');
      card.removeAttribute('data-variant-id');
    });
  };

  const setSelectedVariant = (productId, variantId, sourceImg) => {
    document.querySelectorAll(`.catalog-grid-card__item[data-product-id="${productId}"]`).forEach((card) => {
      const group = card.closest('.addon-catalog-app');
      clearGroupSelection(group);

      card.classList.add('selected');
      card.setAttribute('data-variant-id', variantId);

      const cardImg = card.querySelector('img');
      if (sourceImg && cardImg) {
        cardImg.src = sourceImg.currentSrc || sourceImg.src;
        if (sourceImg.srcset) cardImg.srcset = sourceImg.srcset;
      }
    });
  };

  const setupNoThanksButtons = (container) => {
    container.querySelectorAll('[data-label="no-thanks"]').forEach((noThanksEl) => {
      noThanksEl.addEventListener('click', (event) => {
        const group = event.currentTarget.closest('.addon-catalog-app');
        clearGroupSelection(group);

        event.currentTarget.classList.add('selected');
        event.currentTarget.setAttribute('data-variant-id', 'no-thanks');
      });
    });
  };

  const setupVariantPickers = () => {
    // modal-dialog moves itself to be a direct child of <body> on connect
    // (assets/global.js ModalDialog#connectedCallback), so the variant
    // pickers it contains are no longer descendants of `container` by the
    // time this runs — query from `document`, not `container`.
    document.querySelectorAll('.product-addon-catalog__variant').forEach((variantContainer) => {
      const productId = variantContainer.dataset.productId;
      if (!productId) return;

      const modalDialog = variantContainer.closest('modal-dialog');
      const preview = modalDialog?.querySelector('.addon-catalog-modal__preview');
      const items = Array.from(variantContainer.querySelectorAll('.product-addon-catalog__variant-item'));
      const priceEls = modalDialog?.querySelectorAll('.product-addon-catalog__variant-price') || [];
      const previewEls = modalDialog?.querySelectorAll('.addon-catalog-modal__preview-image') || [];

      // Activates a variant everywhere it's represented (thumbnail border,
      // large preview, price, and the matching card + image in the outer
      // grid). Never closes the modal itself — the shopper closes it
      // explicitly (X button, ESC, backdrop click, all handled natively by
      // modal-dialog) once they're done browsing/picking.
      const activateVariant = (variantId) => {
        const selectedItem = items.find((i) => i.dataset.variantId === variantId);

        items.forEach((i) => i.classList.toggle('selected', i === selectedItem));

        priceEls.forEach((priceEl) => {
          priceEl.classList.toggle('is-visible', priceEl.dataset.variantId === variantId);
        });

        previewEls.forEach((previewEl) => {
          previewEl.classList.toggle('is-visible', previewEl.dataset.variantId === variantId);
        });

        setSelectedVariant(productId, variantId, selectedItem?.querySelector('img'));
      };

      items.forEach((item) => {
        item.addEventListener('click', () => {
          if (item.dataset.variantId) activateVariant(item.dataset.variantId);
        });
      });

      preview?.querySelectorAll('.slider-button').forEach((button) => {
        button.addEventListener('click', () => {
          // No variant is `.selected` until the shopper actually picks one
          // (see product-addon-catalog.liquid) — before that, the modal is
          // just showing the first variant's price/preview by default
          // (rendered via `is-visible`, matching `items[0]`), which
          // getCyclicIndex's -1 handling (assets/global.js) accounts for.
          const currentIndex = items.findIndex((i) => i.classList.contains('selected'));
          const direction = parseInt(button.dataset.direction, 10) || 1;
          const nextItem = items[getCyclicIndex(currentIndex, direction, items.length)];

          if (nextItem?.dataset.variantId) activateVariant(nextItem.dataset.variantId);
        });
      });
    });
  };

  document.addEventListener('DOMContentLoaded', init);
})();
